import { getConsent } from "./utils/consentStore";
import { sanitizeTelemetry } from "./utils/telemetryPrivacy";
import type { Analytics } from "firebase/analytics";
import { firebaseConfig, isMobileWebView } from "./firebaseConfig";

let analyticsPromise: Promise<Analytics | null> | null = null;

export const initAnalyticsWithConsent = (): Promise<Analytics | null> => {
  if (!getConsent().analytics || import.meta.env.VITE_EXTERNAL_ANALYTICS_ENABLED !== "true" || typeof window === "undefined" || isMobileWebView()) return Promise.resolve(null);
  if (analyticsPromise) return analyticsPromise;

  analyticsPromise = Promise.all([import("firebase/app"), import("firebase/analytics")])
    .then(async ([appModule, analyticsModule]) => {
      if (!(await analyticsModule.isSupported())) return null;
      const app = appModule.getApps().length > 0
        ? appModule.getApp()
        : appModule.initializeApp(firebaseConfig);
      if (!getConsent().analytics) return null;
      // Disable automatic page views: Firebase otherwise captures the raw browser URL.
      return analyticsModule.initializeAnalytics(app, { config: { send_page_view: false, page_location: window.location.origin + window.location.pathname, page_referrer: "" } });
    })
    .catch((error: unknown) => {
      console.warn("Firebase analytics could not be initialized:", error);
      analyticsPromise = null;
      return null;
    });

  return analyticsPromise;
};

export const logFirebaseAnalyticsEvent = async (
  eventName: string,
  params?: Record<string, unknown>
): Promise<void> => {
  const analytics = await initAnalyticsWithConsent();
  if (!analytics || !getConsent().analytics) return;
  const { logEvent } = await import("firebase/analytics");
  if (!getConsent().analytics) return;
  logEvent(analytics, eventName, { ...sanitizeTelemetry(params), page_location: window.location.origin + window.location.pathname, page_referrer: "" });
};

export const setAnalyticsConsent = async (enabled: boolean): Promise<void> => {
  if (enabled) {
    const analytics = await initAnalyticsWithConsent();
    if (!analytics) return;
    const { setAnalyticsCollectionEnabled } = await import("firebase/analytics");
    setAnalyticsCollectionEnabled(analytics, true);
    return;
  }

  if (!analyticsPromise) return;
  const analytics = await analyticsPromise;
  if (!analytics) return;
  const { setAnalyticsCollectionEnabled } = await import("firebase/analytics");
  setAnalyticsCollectionEnabled(analytics, false);
};
